import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";

export default function Navbar() {
  const { totalItems, setIsOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const showSolid = scrolled || !isHome;
  const textColor = showSolid ? "text-[#0A0A0A]" : "text-white";

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-8 sm:top-9 left-0 right-0 z-50 transition-all duration-500 ${
        showSolid
          ? "bg-white backdrop-blur-xl border-b border-[#E5E5E5]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="transition-all duration-300"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_padel-shop-4/artifacts/98zuvpdj_Wordmark%20%2B%20dot%20-%20White.svg"
              alt="Padlr."
              className={`h-5 sm:h-6 transition-all duration-300 ${showSolid ? "invert" : ""}`}
            />
          </Link>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-10">
            <Link
              to="/shop"
              data-testid="nav-shop-link"
              className={`text-sm tracking-[0.15em] uppercase ${textColor} hover:opacity-60 transition-all duration-300`}
            >
              Shop
            </Link>
            <Link
              to="/about"
              data-testid="nav-about-link"
              className={`text-sm tracking-[0.15em] uppercase ${textColor} hover:opacity-60 transition-all duration-300`}
            >
              About
            </Link>
          </div>

          {/* Cart + Mobile Toggle */}
          <div className="flex items-center gap-4">
            <button
              data-testid="nav-cart-button"
              onClick={() => setIsOpen(true)}
              className={`relative ${textColor} hover:opacity-60 transition-all duration-300`}
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {totalItems > 0 && (
                <span
                  data-testid="cart-badge"
                  className="absolute -top-2 -right-2 w-4 h-4 bg-[#8B6F47] text-white text-[10px] flex items-center justify-center"
                  style={{ fontStyle: "normal" }}
                >
                  {totalItems}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden ${textColor} transition-colors duration-300`}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-white border-t border-[#E5E5E5]"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            <Link
              to="/shop"
              className="text-lg tracking-[0.1em] uppercase text-[#0A0A0A]"
            >
              Shop
            </Link>
            <Link
              to="/about"
              className="text-lg tracking-[0.1em] uppercase text-[#0A0A0A]"
            >
              About
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
